import { Modal } from 'components/Modal';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { ModalBody } from 'components/Modal/ModalBody';
import map from 'lodash/map';
import { ModalActions } from 'components/Modal/ModalActions';
import { Button } from 'components/Button';
import { ArrowLeftLongIcon } from 'icons/outline';
import { TextField } from 'components/TextField';
import { intersection } from 'lodash';
import { Checkbox } from 'components/Checkbox';
import { Box } from 'components/Box';
import noop from 'lodash/noop';
import filter from 'lodash/filter';
import { HiringUpdateFlowContext } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateContext';
import { HiringUpdateFlowSteps } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateFlow.constants';
import { InputInstructions } from 'features/EmployerHiringUpdateFlow/InputInstructions';

const REASON_OPTIONS = [
  "I just posted the job / It's too soon",
  "I'm still recruiting / reviewing applicants",
  'Did not receive enough applicants',
  'Candidates were not responsive',
  'Candidates did not show up for screens or interviews',
  'Candidates were not professional',
  'Candidates did not have the right experience',
  'Candidates did not have the right education/degrees',
  'Candidates did not have the right teaching certifications/licenses',
  'Candidates were expecting higher salaries/compensation',
  'Candidates were expecting different work schedule',
  'Website was difficult to use',
  'No longer needed to hire / Closed the position',
];

export const NotHiringReasonStep = () => {
  const { open, step, notHiringReason, setNotHiringReason, goToNextStep, goToPreviousStep } =
    useContext(HiringUpdateFlowContext);

  const [otherReason, setOtherReason] = useState('');
  const [showReasonTextField, setShowReasonTextField] = useState(false);

  const handleChange = (reason: string) => {
    setNotHiringReason((prev) =>
      prev.includes(reason) ? filter(prev, (item) => item !== reason) : [...prev, reason],
    );
  };

  const handleOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (!checked) {
      setOtherReason('');
      setNotHiringReason((prev) => intersection(prev, REASON_OPTIONS));
    } else {
      setNotHiringReason((prev) => [...prev, otherReason]);
    }

    setShowReasonTextField(checked);
  };

  const handleOtherTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const optionReasons = intersection(REASON_OPTIONS, notHiringReason);

    setOtherReason(value);
    setNotHiringReason([...optionReasons, value]);
  };

  useEffect(() => {
    if (!open) {
      setShowReasonTextField(false);
      setOtherReason('');
    }
  }, [open]);

  return (
    <Modal
      size="lg"
      panelProps={{
        className: 'max-w-[594px]',
      }}
      open={open && step === HiringUpdateFlowSteps.REASON_NOT_EARLYDAY}
      heading={
        <span className="text-primary-pure-blue">
          Why didn&apos;t you hire anyone with EarlyDay yet?
        </span>
      }
      actionRequired
      onClose={noop}
    >
      <ModalBody>
        <InputInstructions>Select all that apply.</InputInstructions>

        <Box className="mt-6 !py-4">
          <div className="flex flex-col gap-2">
            {map(REASON_OPTIONS, (reason) => (
              <Checkbox
                id={reason}
                key={reason}
                checked={notHiringReason.includes(reason)}
                onChange={() => handleChange(reason)}
                value={reason}
                text={<span className="text-neutral-dark">{reason}</span>}
              />
            ))}

            <Checkbox
              id="other"
              checked={showReasonTextField}
              onChange={handleOtherChange}
              value="Other"
              text={<span className="text-neutral-dark">Other</span>}
            />

            {(showReasonTextField || null) && (
              <div className="px-2">
                <TextField
                  placeholder="Enter reason"
                  value={otherReason}
                  onChange={handleOtherTextFieldChange}
                />
              </div>
            )}
          </div>
        </Box>
      </ModalBody>

      <ModalActions>
        <Button
          type="button"
          size="lg"
          fullWidth
          onClick={goToNextStep}
          disabled={
            (showReasonTextField && !otherReason) ||
            (!showReasonTextField && !notHiringReason.length)
          }
        >
          Confirm
        </Button>
        <Button type="button" size="lg" variant="outline" onClick={goToPreviousStep} fullWidth>
          <ArrowLeftLongIcon className="mr-2" />
          <span>Go back</span>
        </Button>
      </ModalActions>
    </Modal>
  );
};
